import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Avatar,
  Grid,
  Alert,
  Chip,
} from '@mui/material';
import { Person, Payment } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { apiService } from '../services/api';
import CampaignStatusChip from '../components/CampaignStatusChip';
import { normalizeImageUrl } from '../utils/imageUtils';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, error } = useAuthStore();
  const { user: telegramUser } = useTelegramWebApp();
  const [latestCampaign, setLatestCampaign] = useState<any>(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [recentCampaigns, setRecentCampaigns] = useState<any[]>([]);
  const [recentCampaignsLoading, setRecentCampaignsLoading] = useState(false);
  // Баланс общего счёта через AccountManagement API
  const [yandexBalance, setYandexBalance] = useState<{
    balance: number | null;
    balance_available: number | null;
    formatted_balance: string | null;
    formatted_balance_available: string | null;
    total_formatted: string | null;
    currency: string;
    account_id: string | null;
    has_balance: boolean;
    method: string;
  } | null>(null);

  const handleNavigateToCampaigns = () => {
    navigate('/campaigns');
  };

  const handlePayCampaign = (campaignBudget: string, campaignId?: number) => {
    const params = new URLSearchParams();
    params.append('amount', campaignBudget);
    if (campaignId) {
      params.append('campaign_id', campaignId.toString());
    }
    navigate(`/topup?${params.toString()}`);
  };

  const handleNavigateToProfile = () => {
    navigate('/profile');
  };

  // Загружаем последнюю кампанию
  useEffect(() => {
    const loadLatestCampaign = async () => {
      if (!telegramUser?.id) {
        console.log('HomePage: No telegram user ID, skipping campaign load');
        return;
      }
      
      console.log('HomePage: Loading latest campaign for user:', telegramUser.id);
      setCampaignLoading(true);
      try {
        const campaign = await apiService.getLatestCampaign(telegramUser.id);
        console.log('HomePage: Latest campaign loaded:', campaign);
        setLatestCampaign(campaign);
      } catch (err) {
        console.error('HomePage: Failed to load latest campaign:', err);
      } finally {
        setCampaignLoading(false);
      }
    };

    loadLatestCampaign();
  }, [telegramUser?.id]);

  // Загружаем последние 3 кампании
  useEffect(() => {
    const loadRecentCampaigns = async () => {
      if (!telegramUser?.id) {
        console.log('HomePage: No telegram user ID, skipping recent campaigns load');
        return;
      }
      
      console.log('HomePage: Loading recent campaigns for user:', telegramUser.id);
      setRecentCampaignsLoading(true);
      try {
        const campaigns = await apiService.getCampaigns(telegramUser.id);
        console.log('HomePage: Recent campaigns loaded:', campaigns);
        // Берем только последние 3 кампании
        setRecentCampaigns(campaigns.slice(0, 3));
      } catch (err) {
        console.error('HomePage: Failed to load recent campaigns:', err);
      } finally {
        setRecentCampaignsLoading(false);
      }
    };

    loadRecentCampaigns();
  }, [telegramUser?.id]);

  // Загружаем баланс общего счёта через AccountManagement API
  useEffect(() => {
    const loadYandexBalance = async () => {
      if (!telegramUser?.id) {
        console.log('HomePage: No telegram user ID, skipping balance load');
        return;
      }
      
      console.log('HomePage: Loading Yandex Direct balance for user:', telegramUser.id);
      try {
        const balanceData = await apiService.getYandexDirectAccountBalance(telegramUser.id);
        console.log('HomePage: Yandex Direct balance loaded:', balanceData);
        if (balanceData) {
          setYandexBalance(balanceData);
        }
      } catch (err) {
        console.log('HomePage: Yandex Direct balance not available:', err);
      }
    };

    loadYandexBalance();
  }, [telegramUser?.id]);

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography variant="h6">Загрузка...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box textAlign="center">
          <Typography variant="h6">Ошибка загрузки</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Попробуйте перезапустить приложение
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography variant="h6">Авторизация...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Подключение к Telegram
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Welcome Section */}
      <Card elevation={0} sx={{ mb: 3, background: 'rgb(248, 242, 255)' }}>
        <CardContent sx={{ textAlign: 'center', color: '#000000' }}>
          <Avatar
            src={normalizeImageUrl(user?.photo_url)}
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 2,
              border: '3px solid rgba(0,0,0,0.1)',
            }}
          >
            <Person sx={{ fontSize: 40 }} />
          </Avatar>
          
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Добро пожаловать, {user?.first_name}!
          </Typography>
          
          <Typography variant="body2" sx={{ opacity: 0.7, color: '#000000' }}>
            Ваш баланс: {yandexBalance?.total_formatted || yandexBalance?.formatted_balance || '0 руб.'}
          </Typography>
        </CardContent>
      </Card>

      {/* Latest Campaign */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {(() => {
            console.log('HomePage: Rendering campaign section', {
              campaignLoading,
              latestCampaign,
              hasLatestCampaign: !!latestCampaign
            });
            return null;
          })()}
          {campaignLoading ? (
            <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Загружаем информацию о кампании...
                </Typography>
              </CardContent>
            </Card>
          ) : latestCampaign && (
            <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
              <CardContent sx={{ py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Payment 
                    sx={{ 
                      fontSize: 32, 
                      color: '#000000', 
                      mr: 2 
                    }} 
                  />
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                      Кампания ожидает оплаты
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {latestCampaign.name}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Бюджет: ${latestCampaign.budget}₽`} 
                    size="small" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={latestCampaign.goal === 'sales' ? 'Продажи' : 
                           latestCampaign.goal === 'leads' ? 'Лиды' : 
                           latestCampaign.goal === 'awareness' ? 'Узнаваемость' : latestCampaign.goal} 
                    size="small" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={latestCampaign.geo === 'moscow' ? 'Москва' : 
                           latestCampaign.geo === 'spb' ? 'Санкт-Петербург' : 
                           latestCampaign.geo === 'russia' ? 'Россия' : latestCampaign.geo} 
                    size="small" 
                    variant="outlined" 
                  />
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 3 }}
                >
                  {latestCampaign.site_url}
                </Typography>
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Payment />}
                  onClick={() => handlePayCampaign(latestCampaign.budget, latestCampaign.id)}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                    color: '#ffffff !important',
                    boxShadow: 'none',
                    '&:hover': { 
                      background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                      opacity: 0.9,
                      boxShadow: 'none'
                    }
                  }}
                >
                  Оплатить кампанию
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Recent Campaigns */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Последние кампании
          </Typography>
          
          {recentCampaignsLoading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Загружаем кампании...
              </Typography>
            </Box>
          ) : recentCampaigns.length > 0 ? (
            <>
              {recentCampaigns.map((campaign, index) => (
                <Box 
                  key={campaign.id} 
                  sx={{ 
                    py: 2, 
                    borderBottom: index < recentCampaigns.length - 1 ? '1px solid #ececfc' : 'none' 
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000' }}>
                      {campaign.name}
                    </Typography>
                    <CampaignStatusChip 
                      status={campaign.status}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Бюджет: ${campaign.budget}₽`} 
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: '#ececfc', color: '#514b81' }}
                    />
                    <Chip 
                      label={campaign.goal === 'sales' ? 'Продажи' : 
                             campaign.goal === 'leads' ? 'Лиды' : 
                             campaign.goal === 'awareness' ? 'Узнаваемость' : campaign.goal} 
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: '#ececfc', color: '#514b81' }}
                    />
                  </Box>
                  
                  {campaign.site_url && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {campaign.site_url}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => navigate(`/campaigns/${campaign.id}`)}
                      sx={{
                        borderColor: '#283655',
                        color: '#000000',
                        '&:hover': {
                          borderColor: '#283655',
                          backgroundColor: 'rgba(40, 54, 85, 0.04)'
                        }
                      }}
                    >
                      Подробнее
                    </Button>
                  </Box>
                </Box>
              ))}
              
              <Button
                variant="contained"
                fullWidth
                onClick={handleNavigateToCampaigns}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: '#000000',
                  color: '#ffffff',
                  '&:hover': { 
                    bgcolor: '#1e2a3f'
                  }
                }}
              >
                Смотреть все
              </Button>
              
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/campaigns/create')}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                  color: '#ffffff !important',
                  boxShadow: 'none',
                  '&:hover': { 
                    background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                    opacity: 0.9,
                    boxShadow: 'none'
                  }
                }}
              >
                Создать кампанию
              </Button>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                У вас пока нет кампаний
              </Typography>
              
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/campaigns/create')}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                  color: '#ffffff !important',
                  boxShadow: 'none',
                  '&:hover': { 
                    background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                    opacity: 0.9,
                    boxShadow: 'none'
                  }
                }}
              >
                Создать кампанию
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Profile Quick Access */}
      <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ваш профиль
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Просмотрите и отредактируйте информацию о себе
          </Typography>
          
          <Button
            variant="outlined"
            onClick={handleNavigateToProfile}
            fullWidth
            sx={{
              color: '#000000',
              borderColor: '#283655',
              '&:hover': {
                color: '#000000',
                borderColor: '#283655',
                bgcolor: 'rgba(40, 54, 85, 0.04)'
              }
            }}
          >
            Перейти в профиль
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default HomePage; 